import { memo, useMemo } from 'react';

import dayjs from 'dayjs';
import { Bot } from 'lucide-react';

import Chip from '@mui/material/Chip';

import { DomainBotsActionsColumn } from './columns/DomainBotsActionsColumn';

import css from './DomainBotsPanel.module.css';

import { CardHeader } from 'v2/components/card/cardHeader/CardHeader';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { CreateTelegramAuthBotButton } from 'v2/pages/domain/telegramAuthBots/createTelegramAuthBotButton/CreateTelegramAuthBotButton';
import { TableColumn } from 'v2/types';
import { TelegramAuthBotModel } from 'v2/types/TelegramAuthBotModel';

interface DomainBotsPanelProps {
  domainId: string;
  bots: TelegramAuthBotModel[];
  onRevalidate: VoidFunction;
}

export const DomainBotsPanel = memo<DomainBotsPanelProps>(({ domainId, bots, onRevalidate }) => {
  const columns: TableColumn<TelegramAuthBotModel>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        ellipsis: true,
        render: (row) => (row.name ? row.name : <span className={css.botIdFallback}>Bot #{row.botId}</span>),
      },
      {
        key: 'botId',
        label: 'Bot ID',
        render: (row) => <span className={css.botIdMono}>{row.botId}</span>,
      },
      {
        key: 'status',
        label: 'Status',
        align: 'center',
        render: (row) => (
          <Chip
            className="font-medium"
            color={row.isActive ? 'success' : 'default'}
            label={row.isActive ? 'Active' : 'Inactive'}
            size="small"
          />
        ),
      },
      {
        key: 'createdAt',
        label: 'Created At',
        render: (row) => dayjs(row.createdAt).format('DD.MM.YYYY HH:mm'),
      },
      {
        key: 'actions',
        align: 'right',
        render: (row) => (
          <div className={css.botActions}>
            <DomainBotsActionsColumn bot={row} domainId={domainId} onRevalidate={onRevalidate} />
          </div>
        ),
      },
    ],
    [domainId, onRevalidate]
  );

  return (
    <div className={css.domainBotsPanel}>
      <CardHeader
        title="Telegram Auth Bots"
        icon={Bot}
        subheader
        className={css.domainBotsPanelHeader}
        actions={<CreateTelegramAuthBotButton domainId={domainId} onSuccess={onRevalidate} />}
      />

      <DataTable className={css.domainBotsTable} columns={columns} data={bots} />
    </div>
  );
});

DomainBotsPanel.displayName = 'DomainBotsPanel';
